import { Button, Icon, Flex, Tooltip } from "../";
import { Popover } from "../../internal/components/Popover";
import { usePopoverContext } from "../../internal/components/Popover/internal/usePopoverContext";
import { core } from "@servicetitan/hammer-token";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactNode,
  Ref,
  useContext,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useTrackingId, useMergeRefs } from "../../hooks";
import cx from "classnames";
import styles from "./Page.module.scss";
import SegmentSVG from "@servicetitan/hammer-icon/mdi/round/segment.svg";
import RespOpen from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_down.svg";
import RespClose from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_up.svg";
import OpenSVG from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";
import CloseSVG from "@servicetitan/hammer-icon/mdi/round/chevron_left.svg";
import { PageSideBarContext } from "./PageSidebarContext";
import { PageContext, PageContextProps } from "./PageContext";

function AdaptiveSidebarContent({
  children,
  className,
  innerRef,
}: {
  children: ReactNode;
  className?: string;
  innerRef?: Ref<HTMLDivElement>;
}) {
  const { closePopover } = usePopoverContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([containerRef, innerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleClick = (e: Event) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest("a")) closePopover();
    };
    el.addEventListener("click", handleClick);
    return () => el.removeEventListener("click", handleClick);
  }, [closePopover]);

  return (
    <Flex direction="column" className={className} ref={mergedRef}>
      {children}
    </Flex>
  );
}

/**
 * Props for the PageSidebar component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type PageSidebarProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Key for storing sidebar collapsed state in localStorage
   * @default "sidebar-collapsed"
   */
  localStorageKey?: string;
  /**
   * Custom tracking ID for analytics
   */
  ["data-tracking-id"]?: string;
  /**
   * Title to display in the adaptive trigger button
   */
  currentPageTitle?: string;
};

/**
 * PageSidebar component for creating collapsible navigation sidebars.
 *
 * Features:
 * - Collapsible sidebar with localStorage persistence
 * - Responsive design with adaptive popover on mobile
 * - Automatic tracking ID generation for analytics
 * - Keyboard navigation and accessibility support
 * - Toggle button with tooltip
 * - Context provider for sidebar state
 * - Mobile-friendly popover navigation
 *
 * @example
 * <Page.Sidebar currentPageTitle="Navigation">
 *   <Page.SidebarHeader>
 *     <Text variant="headline" el="h2">Sidebar Title</Text>
 *   </Page.SidebarHeader>
 *   <SideNav>
 *     <SideNavLink pathname="/">Home</SideNavLink>
 *   </SideNav>
 * </Page.Sidebar>
 */
export const PageSidebar = forwardRef(
  (props: PageSidebarProps, ref: Ref<HTMLDivElement>) => {
    const {
      children,
      className,
      currentPageTitle,
      localStorageKey = "sidebar-collapsed",
      ...rest
    } = props;

    const data = {
      localStorageKey,
    };

    const { pageWidth } = useContext<PageContextProps>(PageContext);

    const [isMobile, setIsMobile] = useState<boolean>(() => {
      const breakpointWidth = +(
        core.primitive?.BreakpointLg?.value?.replace("px", "") ?? 0
      );
      return typeof window !== "undefined"
        ? window.innerWidth < breakpointWidth
        : false;
    });

    useLayoutEffect(() => {
      if (!pageWidth) return;
      const breakpointWidth = +(
        core.primitive?.BreakpointLg?.value?.replace("px", "") ?? 0
      );
      setIsMobile(pageWidth < breakpointWidth);
    }, [pageWidth]);

    const trackingId = useTrackingId({
      name: "PageSidebarButton",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const [collapsed, setCollapsed] = useState<boolean>(() => {
      // getting stored value
      const sidebarState = localStorage.getItem(localStorageKey);
      return sidebarState ? JSON.parse(sidebarState) : false;
    });
    const classNames = cx([styles["sidebar"]], className, {
      [styles["collapsed"]]: collapsed,
    });
    const onToggleButtonClick = () => {
      setCollapsed((prev) => {
        localStorage.setItem(localStorageKey, JSON.stringify(!prev));
        return !prev;
      });
    };

    if (isMobile) {
      return (
        <Popover fillAvailableHeight matchReferenceWidth noPadding>
          <Popover.Trigger>
            {(props) => {
              if (typeof document !== "undefined") {
                if (props["aria-expanded"]) {
                  document.body.style.overflow = "hidden";
                } else {
                  document.body.style.overflow = "auto";
                }
              }
              return (
                <div className={styles["sidebar-adaptive-trigger-container"]}>
                  <Button
                    className={styles["sidebar-adaptive-trigger"]}
                    icon={SegmentSVG}
                    appearance="ghost"
                    {...props}
                  >
                    {!currentPageTitle ? "Menu" : currentPageTitle}
                    <Icon
                      className={styles["sidebar-adaptive-trigger-icon"]}
                      justifyContent="end"
                      svg={props["aria-expanded"] ? RespClose : RespOpen}
                    />
                  </Button>
                </div>
              );
            }}
          </Popover.Trigger>
          <Popover.Content className={styles["sidebar-adaptive-container"]}>
            <AdaptiveSidebarContent
              className={styles["sidebar-adaptive"]}
              innerRef={ref}
            >
              {children}
            </AdaptiveSidebarContent>
          </Popover.Content>
        </Popover>
      );
    }

    return (
      <PageSideBarContext.Provider value={{ collapsed }}>
        <div className={classNames} ref={ref} {...rest}>
          <div className={styles["sidebar-inner"]} aria-hidden={collapsed}>
            <div>{children}</div>
          </div>
          <div className={styles["toggle-button-wrapper"]}>
            <Tooltip placement="right">
              <Tooltip.Trigger className={styles["toggle-button-tooltip"]}>
                <Button
                  size="small"
                  onClick={onToggleButtonClick}
                  className={styles["toggle-button"]}
                  aria-label={collapsed ? "Open Sidebar" : "Close Sidebar"}
                  data-tracking-id={trackingId}
                  icon={collapsed ? OpenSVG : CloseSVG}
                />
              </Tooltip.Trigger>
              <Tooltip.Content>
                {collapsed ? "Open Sidebar" : "Close Sidebar"}
              </Tooltip.Content>
            </Tooltip>
          </div>
        </div>
      </PageSideBarContext.Provider>
    );
  },
);

PageSidebar.displayName = "Page.Sidebar";
